import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, FileText, Database, Clock, UserCheck, Cookie, Sparkles, Bell } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

const PrivacyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FDFBF7] p-4 md:p-8" data-testid="privacy-page">
      <div className="max-w-4xl mx-auto">
        <button
          data-testid="privacy-back-btn"
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-slate-600 hover:text-primary mb-4 md:mb-6 transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour
        </button>

        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-4xl font-bold text-primary-900 font-heading mb-3 md:mb-4 flex items-center gap-3">
            <Shield className="w-8 h-8 md:w-10 md:h-10 text-primary" />
            Politique de confidentialité
          </h1>
          <p className="text-slate-600">Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}</p>
        </div>

        <Card className="mb-6">
          <CardContent className="p-4 md:p-8 prose max-w-none">
            {/* 1 */}
            <h2 className="text-2xl font-bold text-primary-900 mb-4 flex items-center gap-2">
              <Lock className="w-6 h-6 text-primary" />
              1. Responsable du traitement
            </h2>
            <p className="mb-4">
              Le responsable du traitement des données des bénéficiaires est{' '}
              <strong>l'établissement utilisateur</strong> de Carelio.{' '}
              <strong>CREATIVE MEDIA</strong> (éditeur de Carelio, SIRET 507 909 307 00054, 10 Place de
              l'Église, 45450 Donnery) agit en tant que{' '}
              <strong>sous-traitant au sens de l'article 28 du RGPD</strong> pour ces données. Pour les
              données de compte des professionnels (email, identifiants), CREATIVE MEDIA est responsable
              du traitement.
            </p>

            {/* 2 */}
            <h2 className="text-2xl font-bold text-primary-900 mb-4 flex items-center gap-2">
              <Database className="w-6 h-6 text-primary" />
              2. Données collectées
            </h2>
            <h3 className="text-xl font-semibold text-slate-900 mb-3">2.1 Données de compte professionnel</h3>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Nom complet, adresse email, organisation, rôle (directeur, éducateur, etc.)</li>
              <li>Mot de passe (stocké sous forme de hachage <strong>bcrypt</strong>, jamais en clair)</li>
              <li>Photo de profil (uniquement si connexion Google)</li>
              <li>Métadonnées de connexion : date de dernière connexion, compteur de connexions</li>
              <li>Jetons de session (JWT 7 jours et cookies sécurisés <em>httpOnly / SameSite=None / Secure</em> pour Google OAuth)</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mb-3">2.2 Données des bénéficiaires (catégorie particulière — Art. 9 RGPD)</h3>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>État civil : nom, prénom, date de naissance</li>
              <li><strong>Données de santé</strong> : diagnostic, plan d'accompagnement</li>
              <li>Notes de service et observations professionnelles (saisies au clavier ou via dictée vocale)</li>
              <li>Planning : tâches assignées, rendez-vous (date, heure, lieu)</li>
              <li>Pièces jointes générées : exports PDF (notes, tâches, RDV, fiches bénéficiaire)</li>
              <li>Messages internes échangés entre professionnels concernant un bénéficiaire</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mb-3">2.3 Données techniques</h3>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Journaux de connexion et d'accès aux ressources (logs serveur)</li>
              <li>Préférences de consentement aux cookies</li>
              <li>Notifications applicatives (en base et diffusées en temps réel via WebSocket sécurisé <strong>wss://</strong>)</li>
            </ul>

            {/* 3 */}
            <h2 className="text-2xl font-bold text-primary-900 mb-4 flex items-center gap-2">
              <FileText className="w-6 h-6 text-primary" />
              3. Finalités du traitement
            </h2>
            <p className="mb-4">Les données sont traitées pour :</p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>La gestion administrative et le suivi médico-social des bénéficiaires</li>
              <li>La rédaction et l'archivage de notes de service (y compris via <strong>dictée vocale assistée par IA</strong>)</li>
              <li>La planification des tâches et rendez-vous, avec notifications en temps réel</li>
              <li>La génération d'exports PDF (notes individuelles ou groupées, fiches bénéficiaires, rapports de conformité)</li>
              <li>La conformité réglementaire des établissements (Dossier Usager Informatisé)</li>
              <li>La communication interne entre membres d'une équipe pluridisciplinaire</li>
              <li>L'amélioration du service (statistiques agrégées et anonymisées)</li>
            </ul>

            {/* 4 */}
            <h2 className="text-2xl font-bold text-primary-900 mb-4 flex items-center gap-2">
              <UserCheck className="w-6 h-6 text-primary" />
              4. Base légale du traitement
            </h2>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li><strong>Obligation légale</strong> : Dossier Usager Informatisé (DUI) prévu par la réglementation médico-sociale</li>
              <li><strong>Intérêt légitime</strong> : suivi et accompagnement des bénéficiaires par l'établissement</li>
              <li><strong>Exécution d'un contrat</strong> : fourniture du service Carelio aux établissements abonnés</li>
              <li><strong>Consentement</strong> : utilisation de la dictée vocale IA et communication avec les familles</li>
            </ul>

            {/* 5 */}
            <h2 className="text-2xl font-bold text-primary-900 mb-4 flex items-center gap-2">
              <Lock className="w-6 h-6 text-primary" />
              5. Sécurité et hébergement des données
            </h2>
            <div className="bg-primary-50 border-l-4 border-primary p-4 rounded-r-xl mb-4">
              <p className="font-semibold text-primary-900 mb-2">Hébergement sécurisé en Union européenne</p>
              <p className="text-sm">
                Les données applicatives sont stockées dans une base <strong>MongoDB</strong> opérée sur une
                infrastructure hébergée en Union européenne.
              </p>
            </div>
            <p className="mb-4"><strong>Mesures techniques :</strong></p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Chiffrement des échanges en transit (<strong>HTTPS/TLS</strong>) y compris pour le canal temps réel (<strong>WSS</strong>)</li>
              <li>Hachage des mots de passe avec <strong>bcrypt</strong> (jamais stockés ni transmis en clair)</li>
              <li>Politique de mot de passe : minimum 8 caractères, au moins une lettre et un chiffre</li>
              <li>Authentification par jeton JWT signé (HS256, expiration 7 jours) ou cookie de session httpOnly</li>
              <li>Variables secrètes (clé JWT, clés d'API IA) chiffrées et stockées hors du dépôt de code</li>
              <li>Contrôle d'accès par organisation : un utilisateur ne peut accéder qu'aux données de son organisation</li>
              <li>Index uniques sur les identifiants utilisateurs et tokens de session</li>
              <li>Journalisation des accès (logs d'audit côté serveur)</li>
              <li>Sauvegardes régulières</li>
            </ul>
            <p className="mb-4 text-sm text-slate-600">
              Carelio s'engage à notifier l'établissement responsable de tout incident de sécurité dans les
              <strong> 72 heures</strong> conformément à l'article 33 du RGPD.
            </p>

            {/* 6 */}
            <h2 className="text-2xl font-bold text-primary-900 mb-4 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-primary" />
              6. Intelligence artificielle et dictée vocale
            </h2>
            <p className="mb-4">
              Carelio propose une fonctionnalité de <strong>dictée vocale</strong> qui permet d'enregistrer une note orale,
              de la transcrire automatiquement et d'en générer un compte-rendu structuré.
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li><strong>Transcription audio</strong> : modèle <em>Whisper</em> d'OpenAI</li>
              <li><strong>Génération de texte structuré</strong> : modèle <em>Claude Sonnet 4.5</em> d'Anthropic</li>
              <li>Les enregistrements audio sont transmis aux services IA pour traitement immédiat et ne sont pas conservés par Carelio après traitement</li>
              <li>Les contenus envoyés aux modèles IA sont <strong>pseudonymisés</strong> dans la mesure du possible avant transmission</li>
              <li>L'utilisateur reste <strong>seul responsable</strong> de la relecture et de la validation finale des notes générées par IA</li>
              <li>Aucune décision automatisée produisant des effets juridiques n'est prise par l'IA (art. 22 RGPD)</li>
            </ul>

            {/* 7 */}
            <h2 className="text-2xl font-bold text-primary-900 mb-4 flex items-center gap-2">
              <Bell className="w-6 h-6 text-primary" />
              7. Notifications en temps réel
            </h2>
            <p className="mb-4">
              Carelio utilise un canal <strong>WebSocket sécurisé (WSS)</strong> pour diffuser instantanément aux
              utilisateurs connectés les notifications applicatives (nouvelle note, tâche assignée, rendez-vous créé,
              message reçu). Aucune donnée n'est transmise par ce canal sans authentification valide (jeton JWT). Les
              notifications sont également stockées en base pour consultation a posteriori.
            </p>

            {/* 8 */}
            <h2 className="text-2xl font-bold text-primary-900 mb-4 flex items-center gap-2">
              <Cookie className="w-6 h-6 text-primary" />
              8. Cookies et stockage local
            </h2>
            <p className="mb-4">Carelio utilise les éléments suivants côté navigateur :</p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li><strong>localStorage</strong> : conservation du jeton JWT pour maintenir la session sur l'appareil</li>
              <li><strong>Cookie de session</strong> (httpOnly, Secure, SameSite=None) : uniquement en cas de connexion via Google OAuth, durée 7 jours</li>
              <li><strong>Cookie de consentement</strong> : mémorise votre choix concernant la bannière cookies</li>
            </ul>
            <p className="mb-4">
              Aucun cookie publicitaire, aucun cookie de suivi tiers (Google Analytics, Meta Pixel, etc.) n'est déposé
              sur la plateforme.
            </p>

            {/* 9 */}
            <h2 className="text-2xl font-bold text-primary-900 mb-4 flex items-center gap-2">
              <Clock className="w-6 h-6 text-primary" />
              9. Durée de conservation
            </h2>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li><strong>Dossiers usagers</strong> : 20 ans après le dernier contact (Code de la santé publique)</li>
              <li><strong>Notes de service</strong> : durée de la prise en charge + 20 ans</li>
              <li><strong>Comptes professionnels</strong> : durée de l'emploi + 3 ans après la dernière connexion</li>
              <li><strong>Sessions et jetons JWT</strong> : 7 jours maximum</li>
              <li><strong>Logs de connexion</strong> : 6 mois (sécurité)</li>
              <li><strong>Enregistrements audio (dictée)</strong> : non conservés au-delà du traitement temps réel</li>
            </ul>

            {/* 10 */}
            <h2 className="text-2xl font-bold text-primary-900 mb-4 flex items-center gap-2">
              <Eye className="w-6 h-6 text-primary" />
              10. Vos droits (RGPD)
            </h2>
            <p className="mb-4">Vous disposez à tout moment des droits suivants :</p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li><strong>Droit d'accès</strong> : obtenir une copie de vos données</li>
              <li><strong>Droit de rectification</strong> : corriger vos données</li>
              <li><strong>Droit à l'effacement</strong> : demander la suppression de votre compte et données associées (hors obligation légale)</li>
              <li><strong>Droit d'opposition</strong> : vous opposer à un traitement</li>
              <li><strong>Droit à la portabilité</strong> : récupérer vos données dans un format structuré (PDF, JSON)</li>
              <li><strong>Droit de limitation</strong> : limiter temporairement un traitement</li>
              <li><strong>Droit de retirer votre consentement</strong> à tout moment (dictée IA notamment)</li>
            </ul>
            <p className="mb-4">
              Une grande partie de ces droits peut être exercée directement depuis votre tableau de bord (édition,
              suppression, export PDF de vos données). Pour toute demande complémentaire :
              <strong> contact@carelio.fr</strong>.
            </p>

            {/* 11 */}
            <h2 className="text-2xl font-bold text-primary-900 mb-4">11. Sous-traitants et partenaires techniques</h2>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li><strong>Hébergement applicatif et base de données</strong> : Emergent Labs (infrastructure cloud sécurisée)</li>
              <li><strong>Authentification Google (optionnelle)</strong> : Google LLC via l'intégration <em>Emergent Auth</em></li>
              <li><strong>Transcription vocale</strong> : OpenAI (modèle Whisper)</li>
              <li><strong>Génération de texte assistée</strong> : Anthropic (modèle Claude Sonnet 4.5)</li>
            </ul>
            <p className="mb-4 text-sm text-slate-600">
              Tous nos sous-traitants sont sélectionnés sur la base de garanties techniques et organisationnelles
              suffisantes (article 28 RGPD).
            </p>

            {/* 12 */}
            <h2 className="text-2xl font-bold text-primary-900 mb-4">12. Transferts hors UE</h2>
            <p className="mb-4">
              Certains services IA (Anthropic, OpenAI) peuvent impliquer des transferts vers les <strong>États-Unis</strong>.
              Ces transferts sont encadrés par les <strong>clauses contractuelles types</strong> de la Commission
              européenne et le <strong>EU-US Data Privacy Framework</strong>. Les contenus envoyés sont pseudonymisés
              dans la mesure du possible et ne sont pas utilisés par les fournisseurs IA pour entraîner leurs modèles
              (option <em>no-training</em> activée).
            </p>

            {/* 13 */}
            <h2 className="text-2xl font-bold text-primary-900 mb-4">13. Contact et réclamation</h2>
            <p className="mb-4">
              Pour toute question relative à la présente politique de confidentialité ou pour exercer vos droits :
            </p>
            <div className="bg-slate-50 p-4 rounded-xl">
              <p className="mb-1"><strong>Éditeur :</strong> CREATIVE MEDIA — SIRET 507 909 307 00054</p>
              <p className="mb-1"><strong>Adresse :</strong> 10 Place de l'Église, 45450 Donnery</p>
              <p className="mb-1"><strong>Email :</strong> <a href="mailto:contact@carelio.fr" className="text-primary underline">contact@carelio.fr</a></p>
            </div>
            <p className="mt-4 text-sm text-slate-600">
              Vous pouvez également introduire une réclamation auprès de la <strong>CNIL</strong> —
              <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-primary underline ml-1">www.cnil.fr</a>.
            </p>

            {/* 14 */}
            <h2 className="text-2xl font-bold text-primary-900 mb-4 mt-6">14. Évolution de la politique</h2>
            <p className="mb-2">
              Carelio étant en phase bêta active, cette politique est susceptible d'évoluer pour refléter les nouvelles
              fonctionnalités ou les évolutions réglementaires. Toute modification substantielle sera notifiée aux
              utilisateurs par email et via la plateforme.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPage;
